import { useState } from 'react';
import { Button, Label, Modal, TextInput, Datepicker } from "flowbite-react";
import useCertificateController from '../controllers/useCertificateController';
// import Sidebar from '../components/Sidebar';

function Certificate() {
  const { 
    certificates, 
    loading, 
    error, 
    addCertificate, 
    updateCertificate, 
    deleteCertificate
  } = useCertificateController();

  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [certificateData, setCertificateData] = useState({
    title: '',
    org: '',
    issue: new Date(),
    expire: new Date(),
    cred_id: '',
    cred_url: ''
  });

  function onCloseModal() {
    setOpenModal(false);
    setIsEditMode(false);
    setCurrentCertificate(null);
    setCertificateData({
      title: '',
      org: '',
      issue: new Date(),
      expire: new Date(),
      cred_id: '',
      cred_url: ''
    });
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCertificateData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleDateChange = (name, date) => {
    setCertificateData(prevData => ({
      ...prevData,
      [name]: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditMode && currentCertificate) {
      await updateCertificate(currentCertificate.id, certificateData);
    } else {
      await addCertificate(certificateData);
    }
    onCloseModal();
  };

  const handleEdit = (certificate) => {
    setCurrentCertificate(certificate);
    setIsEditMode(true);
    setCertificateData({
      title: certificate.title,
      org: certificate.org,
      issue: new Date(certificate.issue),
      expire: certificate.expire ? new Date(certificate.expire) : null,
      cred_id: certificate.cred_id || '',
      cred_url: certificate.cred_url || ''
    });
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      await deleteCertificate(id);
    }
  };

  return (
    <div className='flex'>
      {/* <Sidebar /> */}
      <section className="flex-1 py-5 md:px-5 bg-gray-100 dark:bg-gray-900">
        <div className="grid md:grid-cols-3 grid-cols-2">
          <div className='my-6 md:col-span-2 md:pl-6'>
            <h1 className="text-3xl font-extrabold text-left lg:text-5xl 2xl:text-8xl">
              <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
                Certificate
              </span>
            </h1>

            <p className="max-w-3xl md:mt-6 mt-2 mr-4 md:text-md text-sm text-left text-gray-700 dark:text-white md:text-md">
              Showcase your expertise! Add certifications that validate your skills and boost your professional credibility.
            </p>
          </div>
          
          <div className="mx-auto my-auto md:col-span-1 ml-3">
            <Button color="blue" onClick={() => setOpenModal(true)}><b>+</b> Add Your Certificate</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {loading && <p>Loading certificates...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {certificates.map(cert => (
            <div 
              key={cert.id} 
              className="w-full max-w-sm overflow-hidden bg-white rounded-xl shadow-lg dark:bg-gray-800 cursor-pointer transform transition hover:scale-105 hover:shadow-2xl group"
              // onClick={() => handleEdit(cert)}
            >
              <div className="px-6 py-4 border-l-4 border-blue-500">
                <div className="flex items-center mb-3">
                  <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  <h1 className="text-xl font-bold text-gray-800 dark:text-white">{cert.title}</h1>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <span className="w-20 font-medium text-gray-500 dark:text-gray-400">Issuer</span>
                    <span className="text-gray-700 dark:text-gray-300">: {cert.org}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-20 font-medium text-gray-500 dark:text-gray-400">Issued</span>
                    <span className="text-gray-700 dark:text-gray-300">: {new Date(cert.issue).toLocaleDateString()}</span>
                  </div>
                  {cert.expire && (
                    <div className="flex items-center">
                      <span className="w-20 font-medium text-gray-500 dark:text-gray-400">Expires</span>
                      <span className="text-gray-700 dark:text-gray-300">: {new Date(cert.expire).toLocaleDateString()}</span>
                    </div>
                  )}
                  {cert.cred_id && (
                    <div className="flex items-center">
                      <span className="w-20 font-medium text-gray-500 dark:text-gray-400">Credential ID</span>
                      <span className="text-blue-600 dark:text-blue-400">: {cert.cred_id}</span>
                    </div>
                  )}
                </div>
            
                {cert.cred_url && (
                  <div className="mt-4">
                    <a href={cert.cred_url} target="_blank" rel="noopener noreferrer" 
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400">
                      <span>Verify Credential</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    color="gray"
                    onClick={() => handleEdit(cert)}
                    className="hover:bg-gray-200 dark:hover:bg-gray-700"
                  >
                    Edit
                  </Button>
                  <Button
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(cert.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Modal show={openModal} onClose={onCloseModal} popup className="backdrop-blur-sm bg-white/30">
          <Modal.Header />
          <Modal.Body>
            <form onSubmit={handleSubmit} className="space-y-6 p-5">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">{isEditMode ? 'Edit Certificate' : 'Add Your Certificate'}</h3>
              
              <div>
                <Label htmlFor="title">Title</Label>
                <TextInput id="title" name="title" value={certificateData.title} onChange={handleInputChange} placeholder="Cyber Security Beginners" required />
              </div>

              <div>
                <Label htmlFor="org">Issuing Organization</Label>
                <TextInput id="org" name="org" value={certificateData.org} onChange={handleInputChange} placeholder="Google" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="issue" value="Issue Date" />
                  <Datepicker id="issue" name="issue" selected={certificateData.issue} onChange={(date) => handleDateChange('issue', date)} />
                </div>
                <div>
                  <Label htmlFor="expire" value="Expire Date" />
                  <Datepicker id="expire" name="expire" selected={certificateData.expire} onChange={(date) => handleDateChange('expire', date)} />
                </div>
              </div>

              <div>
                <Label htmlFor="cred_id">Credential ID</Label>
                <TextInput id="cred_id" name="cred_id" value={certificateData.cred_id} onChange={handleInputChange} placeholder="27337" />
              </div>

              <div>
                <Label htmlFor="cred_url">Certificate URL</Label>
                <TextInput id="cred_url" name="cred_url" value={certificateData.cred_url} onChange={handleInputChange} placeholder="https://google.com/mycert" />
              </div>

              <div className="w-full">
                <Button color="blue" type="submit">{isEditMode ? 'Update Certificate' : 'Add Certificate'}</Button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </section>
    </div>
  );
}

export default Certificate;